import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Phone, Clock, Navigation, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const icon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-gold.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

export default function StoreLocator() {
  const [stores, setStores] = useState([]);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);

  useEffect(() => { api.get("/stores").then((r) => { setStores(r.data); setActive(r.data[0]); }).catch(() => {}); }, []);

  const filtered = stores.filter((s) => `${s.name} ${s.city} ${s.state}`.toLowerCase().includes(q.toLowerCase()));
  const center = active ? [active.lat, active.lng] : [22.5, 78.9];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
      <h1 className="font-heading text-3xl sm:text-4xl font-bold">Find a Store</h1>
      <p className="text-muted-foreground mt-1">{stores.length} D K BAZAAR stores near you.</p>

      <div className="mt-8 grid lg:grid-cols-[380px_1fr] gap-6">
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="store-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by city or store…" className="pl-9 h-11" />
          </div>
          <div className="space-y-3 max-h-[520px] overflow-y-auto no-scrollbar">
            {filtered.map((s) => (
              <button key={s.id} data-testid={`store-item-${s.id}`} onClick={() => setActive(s)}
                className={`w-full text-left rounded-xl border p-4 transition-all ${active?.id === s.id ? "border-yellow-500 ring-1 ring-yellow-500 bg-yellow-100/50 dark:bg-yellow-950/30" : "border-slate-200 dark:border-slate-800 hover:border-yellow-400"}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-semibold">{s.name}</h3>
                  {s.is_franchise && <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">Franchise</Badge>}
                </div>
                <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" /> {s.address}</p>
                <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><Clock className="w-3.5 h-3.5" /> {s.hours}</p>
                <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><Phone className="w-3.5 h-3.5" /> {s.phone}</p>
                <Button asChild size="sm" variant="outline" className="mt-3 border-yellow-500 text-yellow-700 hover:bg-yellow-100">
                  <a href={`https://www.openstreetmap.org/directions?to=${s.lat},${s.lng}`} target="_blank" rel="noreferrer" data-testid={`directions-${s.id}`}>
                    <Navigation className="w-3.5 h-3.5 mr-1" /> Directions
                  </a>
                </Button>
              </button>
            ))}
          </div>
        </div>

        <div className="h-[400px] lg:h-[560px] rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800" data-testid="store-map">
          {stores.length > 0 && (
            <MapContainer center={center} zoom={active ? 11 : 5} scrollWheelZoom={false} key={active?.id}>
              <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {stores.map((s) => (
                <Marker key={s.id} position={[s.lat, s.lng]} icon={icon} eventHandlers={{ click: () => setActive(s) }}>
                  <Popup><b>{s.name}</b><br />{s.address}<br />{s.hours}</Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>
      </div>
    </div>
  );
}
